import React from 'react'
import { v4 } from 'uuid'
import { Select } from 'antd'
import type { Option } from '../../../shared/lib/option.no-deps'
import { InfoPopover } from '../ui/InfoPopover'
import { type Nullable, assertExhaustion } from '../../../shared/lib/typeHelpers.no-deps'
import { DropdownArrowIcon } from '../ant-overrides/icon-overrides'
import { Translations } from '../../../shared/i18n/Translations'

type Props = {
    unitTypesToLoopOverOptions: UnitTypeToLoopOverOption[]
    validationErrors: ContentLoopSettingsValidationErrors
    selectedUnitTypeToLoopOver: Option<number>
    selectedFilters: ContentLoopFilterByParent[]
    taskSubjectUnitType: Option<number>
    unitTypesParents: UnitTypesParents
    parameterOptionsForFilteringByParent: ParameterForFilteringByParent[]
    onUnitTypeToLoopOverChange: (unitTypeId: number) => void
    onFilterChange: (value: ContentLoopFilterByParent) => void
    onFilterCleared: (unitTypeId: number) => void
}

export type UnitTypeToLoopOverOption = {
    unitTypeId: number
    unitTypeName: string
    numberOfUnits: number
}

export type ContentLoopFilterByParent =
    | {
          type: 'AskPersonExecutingTask'
          forUnitTypeId: number
          unitFilterId: Nullable<string>
      }
    | { type: 'UseReportSubject'; forUnitTypeId: number }
    | { type: 'FilterByGivenParentUnit'; forUnitTypeId: number; unitId: number }
    | { type: 'FilterByParentUnitTakenFromParameter'; forUnitTypeId: number; parameterId: string }

export type ContentLoopSettingsValidationErrors = { unitTypeToLoopOverError: Option<string> }

export type UnitTypesParents = Array<{
    unitTypeId: number
    parentTypes: ParentUnitType[]
}>

type ParentUnitType = { id: number; name: string; units: UnitOption[] }

export type ParameterForFilteringByParent = { id: string; name: string; unitTypeId: number }

export function ContentLoopSettings(props: Props): JSX.Element {
    const parentUnitTypes =
        props.unitTypesParents.find((unitType) => unitType.unitTypeId === props.selectedUnitTypeToLoopOver)
            ?.parentTypes ?? ([] as ParentUnitType[])
    return (
        <div className="flex flex-col gap-10 pb-6">
            <div className="flex flex-col gap-4">
                <label className="text-7 font-normal m-0">
                    {window.i18n('appcore:ElementLoop.unitTypeToLoopOverLabel')}
                </label>
                <Select
                    className="h-20"
                    value={props.selectedUnitTypeToLoopOver ?? undefined}
                    status={props.validationErrors.unitTypeToLoopOverError ? 'error' : ''}
                    placeholder={window.i18n('appcore:ElementLoop.unitTypesToLoopOverSelectPlaceholder')}
                    options={props.unitTypesToLoopOverOptions
                        .sort((a, b) => a.unitTypeName.localeCompare(b.unitTypeName))
                        .map((option) => ({
                            key: option.unitTypeName,
                            value: option.unitTypeId,
                            label: (
                                <div className="flex flex-row gap-4 font-normal text-7 h-15 py-4 text-grey-700 ant-select-open:text-inherit items-center">
                                    {option.unitTypeName}
                                    <span className="text-grey3 ant-select-open:text-inherit">
                                        {option.numberOfUnits}
                                    </span>
                                </div>
                            ),
                        }))}
                    onChange={(value) => props.onUnitTypeToLoopOverChange(value)}
                    suffixIcon={<DropdownArrowIcon expanded={false} />}
                    showSearch={true}
                    filterOption={(inputValue, option) => {
                        return option.key.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                    }}
                ></Select>
                {props.validationErrors.unitTypeToLoopOverError && (
                    <div className="flex flex-row gap-2 text-red">
                        <span className="text-9">
                            <zaptic-icon icon-name="alert" />
                        </span>
                        <span>{props.validationErrors.unitTypeToLoopOverError}</span>
                    </div>
                )}
            </div>

            {parentUnitTypes.length > 0 && (
                <div className="flex flex-col">
                    <div className="text-7 font-semibold text-grey-700 py-4">
                        {window.i18n('appcore:ElementLoop.filterByParentLabel')}
                    </div>

                    <div className="flex flex-col pt-4 gap-8">
                        {parentUnitTypes.map((parentUnitType) => {
                            const selectedFilterForUnitType = props.selectedFilters.find(
                                (selectedFilter) => selectedFilter.forUnitTypeId === parentUnitType.id
                            )
                            return parentUnitType.units.length > 0 ? (
                                <div key={parentUnitType.id} className="flex flex-col gap-4">
                                    <label className="text-7 font-normal m-0">
                                        {parentUnitType.name}{' '}
                                        {props.taskSubjectUnitType === parentUnitType.id ? (
                                            <span className="text-6 font-semibold text-grey-500 tracking-widest uppercase">
                                                ({window.i18n('appcore:bi.labels.reportSubject')})
                                            </span>
                                        ) : (
                                            <></>
                                        )}
                                    </label>
                                    <FilterBySelect
                                        hasUseReportSubjectOption={props.taskSubjectUnitType === parentUnitType.id}
                                        unitOptions={parentUnitType.units}
                                        unitType={parentUnitType}
                                        parameterOptions={props.parameterOptionsForFilteringByParent.filter(
                                            (p) => p.unitTypeId === parentUnitType.id
                                        )}
                                        selectedValue={selectedFilterForUnitType}
                                        onChange={props.onFilterChange}
                                        onCleared={props.onFilterCleared}
                                    />
                                </div>
                            ) : (
                                <></>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

function FilterBySelect(props: {
    hasUseReportSubjectOption: boolean
    unitOptions: UnitOption[]
    parameterOptions: ParameterOption[]
    unitType: { id: number; name: string }
    selectedValue: Option<ContentLoopFilterByParent>
    onChange: (value: ContentLoopFilterByParent) => void
    onCleared: (unitTypeId: number) => void
}) {
    const specialOptions = [
        {
            value: 'special---askPersonExecutingTask',
            label: <SpecialOptionLabel title={window.i18n('portal:flow.loop.askPersonExecutingTaskToSelect')} />,
        },
        ...(props.hasUseReportSubjectOption
            ? [
                  {
                      value: 'special---useReportSubject',
                      label: <SpecialOptionLabel title={window.i18n('portal:flow.loop.useReportSubject')} />,
                  },
              ]
            : []),
    ]

    const validValues: string[] = [
        ...specialOptions.map((option) => option.value),
        ...props.parameterOptions.map((option) => `parameter---${option.id}`),
        ...props.unitOptions.map((option) => `unit---${option.id}`),
    ]

    const mappedSelectedValue = props.selectedValue
        ? mapContentLoopFilterByParentToOptionValue(props.selectedValue)
        : undefined

    const selectedValue =
        mappedSelectedValue && validValues.includes(mappedSelectedValue) ? mappedSelectedValue : undefined

    const selectorIsParameter = selectedValue?.startsWith('parameter---')

    const ref = React.useRef<HTMLDivElement>(null)
    const onSelect = React.useCallback(() => ref.current?.blur(), [])

    return (
        <>
            <Select
                ref={ref}
                className="h-20"
                placeholder={window.i18n('appcore:ElementLoop.unitFilterSelectPlaceholder')}
                showSearch={true}
                allowClear={true}
                options={[
                    ...specialOptions,
                    ...(props.parameterOptions.length > 0
                        ? [
                              {
                                  label: <ParameterOptionsGroupLabel />,
                                  options: props.parameterOptions.map((option) => ({
                                      value: `parameter---${option.id}`,
                                      label: <ParameterOptionLabel name={option.name} />,
                                  })),
                              },
                          ]
                        : []),
                    {
                        label: (
                            <UnitGroupOptionsLabel
                                unitTypeName={props.unitType.name}
                                unitTypeUnitsCount={props.unitOptions.length}
                                showDivider={props.parameterOptions.length === 0}
                            />
                        ),
                        options: props.unitOptions
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((option) => ({
                                value: `unit---${option.id}`,
                                label: <UnitOptionLabel name={option.name} />,
                            })),
                    },
                ]}
                onSelect={onSelect}
                value={selectedValue}
                onChange={(value) => {
                    if (!value) {
                        props.onCleared(props.unitType.id)
                        return
                    }
                    // we need to generate unique id for UserSelectable filter so we can distinguish it from other filters of this type
                    const filterId = v4()
                    props.onChange(mapOptionValueToContentLoopFilterByParent(props.unitType.id, filterId, value))
                }}
                filterOption={(inputValue, option) => {
                    if (!option.label.props.name) return false

                    const label = option.label.props?.name as string
                    return label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                }}
                suffixIcon={<DropdownArrowIcon expanded={false} />}
            />
            {selectorIsParameter ? <ParameterNotDefinedNotice /> : ''}
        </>
    )
}

type UnitOption = { name: string; id: number }
type ParameterOption = { name: string; id: string }

function SpecialOptionLabel(props: { title: string }) {
    return <span className="font-normal text-7 h-20 p-4 text-grey-700 ant-select-open:text-inherit">{props.title}</span>
}

function ParameterOptionsGroupLabel(_: {}) {
    return (
        <div className="flex flex-col gap-8">
            <div className="h-px bg-grey-300"></div>
            <div className="flex flex-row gap-2">
                <span className="text-6 text-grey-500 font-medium tracking-widest uppercase">
                    {window.i18n('appcore:workflowParameters.titles.main')}
                </span>
                <span className="flex content-center h-7 flex-wrap">
                    <InfoPopover size="veryTiny" title={window.i18n('appcore:ElementLoop.parametersTooltipInfo')} />
                </span>
            </div>
        </div>
    )
}

function ParameterOptionLabel(props: { name: string }) {
    return (
        <div className="flex flex-row gap-4 font-normal text-7 h-20 py-4 text-grey-700 ant-select-open:text-inherit items-center">
            <span className="flex flex-wrap content-center">
                <zaptic-icon icon-name="unit-parameter" class="h-12 aspect-square" />
            </span>
            <div className="h-10 text-ellipsis">{props.name}</div>
        </div>
    )
}

function UnitGroupOptionsLabel(props: { unitTypeName: string; unitTypeUnitsCount: number; showDivider: boolean }) {
    return (
        <div className="flex flex-col gap-8">
            {props.showDivider ? <div className="h-px bg-grey-300"></div> : <div className="h-px"></div>}
            <span className="text-6 text-grey-500 font-medium tracking-widest">
                {props.unitTypeName.toUpperCase()} ({props.unitTypeUnitsCount})
            </span>
        </div>
    )
}

function UnitOptionLabel(props: { name: string }) {
    return (
        <div className="flex flex-row gap-4 font-normal text-7 h-20 py-4 text-grey-700 ant-select-open:text-inherit items-center">
            <span className="flex flex-wrap content-center">
                <zaptic-icon
                    class="h-12 p-1.5 border-px bg-white border-grey-100 aspect-square rounded-full"
                    icon-name="unit-cube"
                />
            </span>
            <div className="h-10 text-ellipsis">{props.name}</div>
        </div>
    )
}

function mapContentLoopFilterByParentToOptionValue(filter: ContentLoopFilterByParent): string {
    if (filter.type === 'AskPersonExecutingTask') {
        return 'special---askPersonExecutingTask'
    } else if (filter.type === 'UseReportSubject') {
        return 'special---useReportSubject'
    } else if (filter.type === 'FilterByGivenParentUnit') {
        return `unit---${filter.unitId}`
    } else if (filter.type === 'FilterByParentUnitTakenFromParameter') {
        return `parameter---${filter.parameterId}`
    }
    assertExhaustion(filter)
}

function mapOptionValueToContentLoopFilterByParent(
    unitTypeId: number,
    unitFilterId: Nullable<string>,
    value: string
): Option<ContentLoopFilterByParent> {
    const [type, id] = value.split('---')
    switch (type) {
        case 'special':
            if (id === 'askPersonExecutingTask') {
                return { type: 'AskPersonExecutingTask', forUnitTypeId: unitTypeId, unitFilterId }
            }
            return { type: 'UseReportSubject', forUnitTypeId: unitTypeId }
        case 'unit':
            return { type: 'FilterByGivenParentUnit', forUnitTypeId: unitTypeId, unitId: parseInt(id, 10) }
        case 'parameter':
            return {
                type: 'FilterByParentUnitTakenFromParameter',
                forUnitTypeId: unitTypeId,
                parameterId: id,
            }
    }
}

function ParameterNotDefinedNotice() {
    return (
        <span className="flex gap-2 items-center text-grey-500">
            <zaptic-icon icon-name="info" class="block h-6 aspect-square" />
            {Translations.t('appcore:ElementLoop.parameterNotDefinedNotice')}
        </span>
    )
}
